import React, {useEffect, useMemo, useState} from 'react';
import CatalogPanelMode from './modes/CatalogPanelMode';
import PosScanMode from './modes/PosScanMode';
import QuickSearchMode from './modes/QuickSearchMode';
import TilesCatalogMode from './modes/TilesCatalogMode';
import {
    deriveProductPickerBrands,
    getStoredProductPickerMode,
    isProductPickerMode,
    normalizeProductPickerList,
    normalizeServicePickerList,
    resolveProductPickerMode,
} from './modeStorage';
import {
    ProductPickerCategory,
    ProductPickerMode,
    ProductPickerProps,
} from './types';
import AppStore from '../../app.store';

const deriveCategories = (products: ReturnType<typeof normalizeProductPickerList>): ProductPickerCategory[] => {
    const map = new Map<number, ProductPickerCategory>();
    products.forEach((product) => {
        const id = Number(product.category_id) || 0;
        if (!id) return;
        const current = map.get(id);
        if (current) {
            current.count = (current.count || 0) + 1;
        } else {
            map.set(id, {
                id,
                name: product.category || `Категория #${id}`,
                count: 1,
            });
        }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
};

const ProductPicker = ({
    products,
    categories,
    mode,
    context,
    entityType = 'goods',
    singlePick,
    disabled,
    loading,
    placeholder,
    onPick,
    autoFocus,
    compact,
    fetchProductDetails,
    forceProductDetails,
}: ProductPickerProps) => {
    const [storedMode, setStoredMode] = useState<ProductPickerMode>(() =>
        getStoredProductPickerMode(context),
    );

    const isService = entityType === 'service';
    const resolvedSinglePick = singlePick ?? isService;

    useEffect(() => {
        const onModeChange = (event: Event) => {
            const detail = (event as CustomEvent<{mode?: ProductPickerMode; context?: string}>).detail;
            if (!detail?.mode || !isProductPickerMode(detail.mode)) return;
            if (detail.context && context && detail.context !== context) return;
            if (!detail.context && context) return;
            setStoredMode(detail.mode);
        };
        window.addEventListener('visor:product-picker-mode', onModeChange as EventListener);
        return () => window.removeEventListener('visor:product-picker-mode', onModeChange as EventListener);
    }, [context]);

    let resolvedMode = resolveProductPickerMode({
        mode,
        context,
        modes: AppStore.businessSettings?.product_picker_modes,
        legacyMode: AppStore.businessSettings?.product_picker_mode,
        storedMode,
    });

    // Scanner / QR flow is for goods; services fall back to quick search
    if (isService && resolvedMode === 'pos_scan') {
        resolvedMode = 'quick_search';
    }

    const normalizedProducts = useMemo(
        () => (isService
            ? normalizeServicePickerList(products as any[])
            : normalizeProductPickerList(products as any[])),
        [products, isService],
    );
    const resolvedCategories = useMemo(
        () => (categories?.length ? categories : deriveCategories(normalizedProducts)),
        [categories, normalizedProducts],
    );
    const brands = useMemo(
        () => deriveProductPickerBrands(normalizedProducts),
        [normalizedProducts],
    );

    const defaultPlaceholder = isService
        ? 'Выберите услугу'
        : 'Выберите товар';

    const common = {
        products: normalizedProducts,
        categories: resolvedCategories,
        brands,
        context,
        disabled,
        loading,
        compact,
        onPick,
        singlePick: resolvedSinglePick,
        entityType,
        fetchProductDetails: isService ? undefined : fetchProductDetails,
        forceProductDetails: isService ? false : forceProductDetails,
    };

    switch (resolvedMode) {
        case 'catalog_panel':
            return <CatalogPanelMode {...common} />;
        case 'pos_scan':
            return (
                <PosScanMode
                    {...common}
                    placeholder={placeholder || defaultPlaceholder}
                    autoFocus={autoFocus ?? !compact}
                />
            );
        case 'tiles_catalog':
            return <TilesCatalogMode {...common} />;
        case 'quick_search':
        default:
            return (
                <QuickSearchMode
                    {...common}
                    placeholder={placeholder || defaultPlaceholder}
                    autoFocus={autoFocus}
                />
            );
    }
};

export default ProductPicker;
